if (!customElements.get('section-vidstack-video')) {
  customElements.define(
    'section-vidstack-video',
    class SectionVidstackVideo extends HTMLElement {
      constructor() {
        super();
        this.player = null;
        this.vidstackPlayer = null;
        this.vidstackLayout = null;
        this.media = null;
        this.revealed = false;
        this.starting = false;
        this.resourcesPromise = null;
        this.observer = null;
        this.mq = null;
        this.onCoverClick = this.onCoverClick.bind(this);
        this.onMqChange = this.onMqChange.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.cover = this.querySelector('.vv-cover');
        this.playerTarget = this.querySelector('.vv-player');
        this.media = this.readJson('[data-vv-media]');
        if (this.media?.autoplay && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.media.autoplay = false;
          this.classList.remove('is-autoplay');
        }
        if (this.classList.contains('is-autoplay')) this.suppressCover();
        this.addEventListener('click', this.onCoverClick);
        this.querySelectorAll('.vv-cover-video').forEach((video) => this.prepareCoverFrame(video));
        this.preloadWhenVisible();

        const desktopSrc = this.media?.desktop?.src;
        const mobileSrc = this.media?.mobile?.src;
        if (desktopSrc && mobileSrc && desktopSrc !== mobileSrc) {
          this.mq = window.matchMedia('(max-width: 590px)');
          this.mq.addEventListener('change', this.onMqChange);
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.mq?.removeEventListener('change', this.onMqChange);
        this.removeEventListener('click', this.onCoverClick);
        if (this.player) {
          this.player.pause?.();
          this.player.destroy?.();
          this.player = null;
        }
      }

      readJson(selector) {
        const el = this.querySelector(selector);
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (error) {
          console.error('Vidstack video config could not be read', error);
          return null;
        }
      }

      onCoverClick(event) {
        if (!event.target.closest('.vv-cover')) return;
        event.preventDefault();
        this.playFromCover();
      }

      currentMedia() {
        const desktop = this.media?.desktop;
        const mobile = this.media?.mobile;
        if (window.matchMedia('(max-width: 590px)').matches && mobile?.src) return mobile;
        return desktop;
      }

      preloadWhenVisible() {
        const onVisible = () => {
          this.observer?.disconnect();
          if (this.media?.autoplay) {
            this.playFromCover();
            return;
          }
          this.loadResources();
        };

        if (!('IntersectionObserver' in window)) {
          onVisible();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            onVisible();
          },
          { rootMargin: this.media?.autoplay ? '0px' : '240px', threshold: 0.01 }
        );
        this.observer.observe(this);
      }

      loadResources() {
        if (this.vidstackPlayer) return Promise.resolve(true);
        if (this.resourcesPromise) return this.resourcesPromise;

        this.resourcesPromise = this.fetchResources().catch((error) => {
          console.error('Vidstack video failed to load', error);
          this.resourcesPromise = null;
          return false;
        });
        return this.resourcesPromise;
      }

      async fetchResources() {
        const config = this.readJson('.vv-vidstack-config');
        if (!config?.js) return false;

        this.appendStylesheet(config.css?.player);
        this.appendStylesheet(config.css?.theme);

        const { VidstackPlayer, VidstackPlayerLayout } = await import(config.js);
        this.vidstackPlayer = VidstackPlayer;
        this.vidstackLayout = VidstackPlayerLayout;
        return true;
      }

      appendStylesheet(url) {
        if (!url || document.querySelector(`link[href="${url}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        document.head.appendChild(link);
      }

      prepareCoverFrame(video) {
        const showFrame = () => {
          try {
            if (video.currentTime < 0.05) video.currentTime = 0.05;
          } catch (error) {
            /* Seek before metadata is ready. */
          }
          video.pause();
        };

        if (video.readyState >= 1) showFrame();
        else video.addEventListener('loadedmetadata', showFrame, { once: true });
        video.addEventListener('play', () => video.pause());
      }

      async playFromCover() {
        if (this.revealed || this.starting) return;

        const media = this.currentMedia();
        if (!media?.src || !this.playerTarget) return;

        this.starting = true;
        const ready = await this.loadResources();
        if (!ready || !this.isConnected || !this.vidstackPlayer) {
          this.releaseCover();
          return;
        }

        try {
          if (!this.player) {
            const created = await this.vidstackPlayer.create({
              target: this.playerTarget,
              title: media.title || '',
              src: media.src,
              poster: media.poster || undefined,
              viewType: 'video',
              streamType: 'on-demand',
              logLevel: 'warn',
              playsInline: true,
              autoplay: false,
              muted: Boolean(this.media?.muted),
              controls: true,
              loop: Boolean(this.media?.loop),
              layout: this.vidstackLayout ? new this.vidstackLayout() : undefined,
            });
            this.player = created || this.playerTarget.querySelector('media-player');
            if (!this.player) throw new Error('Vidstack player was not created');
            this.player.addEventListener('play', () => this.revealPlayer(), { once: true });
          }

          await this.startPlayback();
          if (!this.player.paused) this.revealPlayer();
          else this.releaseCover();
        } catch (error) {
          console.error('Vidstack video failed to play', error);
          this.releaseCover();
        }
      }

      suppressCover() {
        if (!this.cover) return;
        this.cover.tabIndex = -1;
        this.cover.setAttribute('aria-hidden', 'true');
      }

      releaseCover() {
        this.starting = false;
        if (!this.media?.autoplay || this.revealed) return;
        this.classList.remove('is-autoplay');
        if (!this.cover) return;
        this.cover.tabIndex = 0;
        this.cover.removeAttribute('aria-hidden');
      }

      async startPlayback() {
        if (!this.player) return;

        const play = async () => {
          try {
            await this.player.play();
          } catch (error) {
            if (error?.name === 'NotAllowedError' && !this.player.muted) {
              this.player.muted = true;
              await this.player.play();
              return;
            }
            throw error;
          }
        };

        try {
          await play();
        } catch (error) {
          if (this.player.hasAttribute('data-can-play')) throw error;
          await new Promise((resolve) => {
            this.player.addEventListener('can-play', () => resolve(), { once: true });
          });
          if (!this.isConnected) return;
          await play();
        }
      }

      revealPlayer() {
        if (this.revealed) return;
        this.revealed = true;
        this.classList.add('is-playing');
        if (this.cover) this.cover.hidden = true;
        this.querySelectorAll('.vv-cover-video').forEach((video) => {
          video.pause();
          video.hidden = true;
        });
      }

      onMqChange() {
        if (!this.player) return;
        const media = this.currentMedia();
        if (!media?.src) return;

        const currentSrc = this.player.currentSrc || this.player.src || '';
        if (currentSrc === media.src) return;

        const time = Number(this.player.currentTime) || 0;
        const wasPlaying = !this.player.paused;
        this.player.src = media.src;
        if (media.poster) this.player.poster = media.poster;
        if (media.title) this.player.title = media.title;
        this.player.currentTime = time;
        if (wasPlaying) this.player.play();
      }
    }
  );
}
